/**
 * Faction vendors — issue #11, and the first sink for the in-run currency (#29).
 *
 * `meta/factions.ts` is 389 lines of complete, correct, zero-sum reputation
 * code that nothing called. docs/13 §3 lists it beside `addAsh` and
 * `grantAbility`; docs/05 §5 argues it is what keeps the multi-era blend
 * coherent, because without politics a Blended run is a palette shuffle rather
 * than a place. This file is the half of that system the player can walk into.
 *
 * THE INTERACTION IS THE ALTAR'S, DELIBERATELY. One thing on the counter at a
 * time, turning over on a clock, taken by standing next to it and pressing
 * interact. docs/13 §5 records that one-offer-on-a-clock is the most
 * Noita-*unlike* and best-received interaction in the build, and docs/11 argues
 * that reusing one strong affordance beats adding a second weak one. A grid of
 * buyable rows would be a third interaction the game has never taught, and it
 * would turn a gamble against your own patience into a problem you solve.
 *
 * WHAT IS SOLD. Modules, and only modules. Each faction's `gives` list also
 * names wards, drills, symbiote cultures and debt contracts — for which
 * `combat/` owns no definitions at all. Stocking a shelf with goods that cannot
 * be delivered is precisely the failure this project keeps repeating (docs/13
 * §3), so the unbuildable half is surfaced as flavour (`knownFor`) and never as
 * a price tag.
 *
 * PLACEMENT. Rest nodes, and not all of them. Rest nodes are already the
 * optional find you get by leaving the vertical spine (`restNode.ts`); putting
 * a vendor at every one would make them a checklist. Pure in `(seed, band,
 * index)` like everything else in world/, so the vendor survives a reload by
 * being rebuilt rather than saved — only the run's ledger of what was bought
 * has to persist.
 *
 * Owns no rendering and no input handling: it exposes state and the integrator
 * drives it, which is what keeps it testable without a browser.
 */

import { hash2 } from '../core/rng';
import type { ModuleDef, ModuleFamily, Rarity } from '../combat/types';
import { FACTION_IDS, getFaction, opposedOf } from '../meta/factions';
import type { FactionId, Standing } from '../meta/factions';
import { addModule, markVendorPurchase, runFactions, spendScrip, vendorSold } from '../meta/runState';
import type { RunState } from '../meta/runState';
import type { RestNode } from './restNode';

/** How close the player must stand to read and buy. Matches the altar's reach. */
export const VENDOR_RANGE = 22;

/**
 * Seconds before the counter turns over.
 *
 * Shorter than the altar's 30. The altar's clock is the run's opening decision
 * and deserves the wait; a vendor is a stall you are browsing with money in
 * your hand, and thirty seconds per item makes a five-item stock a two-minute
 * stand-still in a game about moving.
 */
export const VENDOR_ROTATE_SECONDS = 18;

/**
 * Roughly two rest nodes in five. High enough that a run which explores
 * sideways will meet one; low enough that meeting one is luck rather than a
 * schedule.
 */
export const VENDOR_CHANCE = 0.4;

/** Deepest stock any one family reaches, at `offerTier` 3. */
export const MAX_PER_FAMILY = 3;

/**
 * Base price by rarity, in scrip.
 *
 * Calibrated against `SCRIP_BY_RANK`: a base kill pays 6, an elite 25, a boss
 * 90, so an ordinary run arrives at a vendor holding a few hundred. These
 * numbers buy one good module or two ordinary ones per vendor — enough to be a
 * decision, not enough to be a shopping trip.
 */
export const PRICE_BY_RARITY: Readonly<Record<Rarity, number>> = {
  common: 45,
  uncommon: 80,
  rare: 140,
  exotic: 220,
  relic: 340,
};

/**
 * What standing is worth at the till.
 *
 * The two closed bands are `Infinity` rather than a large number on purpose: a
 * price you could theoretically meet is a door that is merely expensive, and
 * `SHOP_CLOSES_AT` says the door is shut. `Factions.shopOpen` is still the
 * authority — this table only has to agree with it.
 */
export const PRICE_MUL_BY_STANDING: Readonly<Record<Standing, number>> = {
  exalted: 0.6,
  allied: 0.75,
  friendly: 0.9,
  neutral: 1,
  wary: 1.4,
  hostile: Infinity,
  nemesis: Infinity,
};

/** How many of their own a hostile faction has waiting at their abandoned stall. */
export const AMBUSH_MIN = 2;
export const AMBUSH_MAX = 5;

// ------------------------------------------------------------------ placing

/** Everything about a vendor that is pure in the seed. */
export interface VendorPlacement {
  faction: FactionId;
  band: number;
  index: number;
  /** Counter position — what the player stands at. */
  x: number;
  y: number;
  /** Stable per-run identity, and the prefix of every offer id. */
  id: string;
}

/**
 * The vendor at a rest node, or null if this one has nobody trading.
 *
 * Which faction is *not* filtered by standing: a faction that hates you still
 * has a presence in the world, and finding their stall shut (or worse, occupied)
 * is more interesting than the stall not being there. The issue asks for
 * exactly that — hostility with a consequence beyond refusal.
 */
export function vendorForRestNode(seed: number, node: RestNode): VendorPlacement | null {
  const h = hash2(node.band, node.index, seed ^ 0x5ed0ac);
  if ((h % 1000) / 1000 >= VENDOR_CHANCE) return null;
  const faction = FACTION_IDS[(h >>> 11) % FACTION_IDS.length];
  return {
    faction,
    band: node.band,
    index: node.index,
    // Opposite the workbench, so the two services in a rest node are two places
    // rather than one crowded plinth.
    x: node.x - node.doorDir * -18,
    y: node.floorY - 3,
    id: `v:${node.band}:${node.index}:${faction}`,
  };
}

// ------------------------------------------------------------------- stock

export interface VendorOffer {
  /** Stable across a reload — the run's purchase ledger keys on it. */
  id: string;
  moduleId: string;
  name: string;
  family: ModuleFamily;
  rarity: Rarity;
  desc: string;
  /** Price at the *current* standing. Recomputed on read, never stored. */
  price: number;
  /** Standing tier this item appears at: 1 = always, 3 = only for the devoted. */
  depth: 1 | 2 | 3;
  sold: boolean;
}

export interface VendorStatus {
  faction: FactionId;
  /** Display name, e.g. "The Sealed Choir". */
  name: string;
  standing: Standing;
  reputation: number;
  shopOpen: boolean;
  hostile: boolean;
  inRange: boolean;
  /** The one thing on the counter, or null when there is nothing to sell you. */
  offer: VendorOffer | null;
  /** Seconds until the counter turns over. */
  timeToRotate: number;
  /** Unsold items the player may currently see, at this standing. */
  stock: number;
  /** Unsold items that exist but need better standing to reach. */
  locked: number;
  /** The player's balance, so a panel needs no second source for it. */
  scrip: number;
  affordable: boolean;
  /** Goods the faction is known for that nothing in combat/ can deliver. */
  knownFor: readonly string[];
  /** One line for the event ticker when the player walks up. */
  greeting: string;
}

/**
 * An unsold item and whether it can be taken right now.
 *
 * Depth-locked stock is *shown*, not hidden, with the reason attached. A shelf
 * that silently grows as reputation improves teaches nothing; a shelf with two
 * greyed lines reading "needs their favour" is the standing system explaining
 * itself at the point of use.
 */
export interface VendorShelfEntry extends VendorOffer {
  available: boolean;
  /** Empty when the line can be taken. Every refusal names its own cause. */
  refusal: string;
}

export interface VendorPurchase {
  ok: boolean;
  message: string;
  /**
   * The instruction the integrator applies, mirroring `craft`'s outcome:
   * installing a module is combat/'s business, not this file's.
   */
  outcome: { kind: 'module'; moduleId: string } | null;
  paid: number;
}

/** A spawn the integrator is asked to make. world/ does not own the registry. */
export interface AmbushRequest {
  faction: FactionId;
  count: number;
  /** Where they are waiting. */
  x: number;
  y: number;
}

// ------------------------------------------------------------------ vendor

export class FactionVendor {
  readonly faction: FactionId;
  readonly opposedFaction: FactionId;
  readonly x: number;
  readonly y: number;
  readonly id: string;
  /** Module families this faction's `gives` list names. */
  readonly families: readonly ModuleFamily[];
  /** Goods they are known for that combat/ has no definitions for. */
  readonly knownFor: readonly string[];

  inRange = false;
  private offers: VendorOffer[];
  private cursor = 0;
  private timer = VENDOR_ROTATE_SECONDS;
  private ambushed = false;

  constructor(
    readonly placement: VendorPlacement,
    modules: ReadonlyMap<string, ModuleDef>,
  ) {
    this.faction = placement.faction;
    this.opposedFaction = opposedOf(placement.faction);
    this.x = placement.x;
    this.y = placement.y;
    this.id = placement.id;

    const def = getFaction(placement.faction);
    const wanted: ModuleFamily[] = [];
    const flavour: string[] = [];
    for (const g of def.gives) {
      if (g.startsWith('module:')) wanted.push(g.slice(7) as ModuleFamily);
      else flavour.push(g);
    }
    this.families = wanted;
    this.knownFor = flavour;
    this.offers = buildStock(placement, wanted, modules);
  }

  /**
   * Advance the clock and update whether the player is at the counter. `dt` in
   * seconds. `run` is read, never written — buying is the only thing that
   * moves state, and it is an explicit call.
   */
  update(dt: number, playerX: number, playerY: number, run: RunState): void {
    const dx = this.x - playerX;
    const dy = this.y - playerY;
    this.inRange = dx * dx + dy * dy < VENDOR_RANGE * VENDOR_RANGE;

    const shelf = this.available(run);
    if (shelf.length === 0) return;

    this.timer -= dt;
    if (this.timer <= 0) {
      this.timer = VENDOR_ROTATE_SECONDS;
      this.cursor++;
    }
    // The cursor indexes the *available* shelf, which shrinks as things sell
    // and grows as standing improves, so it is normalised on read rather than
    // clamped on write.
    this.cursor %= shelf.length;
  }

  /** Every item this vendor could ever hold, sold ones included. */
  allOffers(): readonly VendorOffer[] {
    return this.offers;
  }

  /**
   * Which line of `shelf()` the clock is currently pointing at, or −1 when
   * there is nothing to point at. The rotation drives the highlight rather than
   * hiding the stock: the player sees the whole counter and one thing at a time
   * is *takeable*, which keeps the altar's "decide before it turns over"
   * pressure without pretending the rest of the shelf is invisible.
   */
  cursorIndex(run: RunState): number {
    const shelf = this.shelf(run);
    if (shelf.length === 0) return -1;
    const takeable = this.available(run);
    if (takeable.length === 0) return -1;
    const current = takeable[this.cursor % takeable.length];
    return shelf.findIndex((e) => e.id === current.id);
  }

  /**
   * The whole counter: every unsold item, priced at the current standing, each
   * carrying the reason it cannot be taken if it cannot.
   */
  shelf(run: RunState): VendorShelfEntry[] {
    const f = runFactions(run);
    const open = f.shopOpen(this.faction);
    const tier = f.offerTier(this.faction);
    const standing = f.standing(this.faction);
    const mul = PRICE_MUL_BY_STANDING[standing];
    const out: VendorShelfEntry[] = [];

    for (const o of this.offers) {
      if (vendorSold(run, o.id)) continue;
      // A closed shop still prices its stock, so the panel can show what you
      // are being refused. Infinity would render as a nonsense number, so the
      // neutral rate stands in and `refusal` carries the truth.
      const price = Math.max(1, Math.round(basePrice(o) * (Number.isFinite(mul) ? mul : 1)));
      let refusal = '';
      if (!open) refusal = `${getFaction(this.faction).name} will not trade with you`;
      else if (o.depth > tier) refusal = 'needs their favour';
      else if (run.inventory.scrip < price) refusal = `costs ${price} scrip`;
      out.push({ ...o, price, available: refusal === '', refusal });
    }
    return out;
  }

  /**
   * What is actually purchasable right now: unsold, and inside the depth this
   * standing has earned. Prices are stamped here because they move with
   * standing and storing them would let the counter disagree with the till.
   */
  available(run: RunState): VendorOffer[] {
    const f = runFactions(run);
    if (!f.shopOpen(this.faction)) return [];
    const tier = f.offerTier(this.faction);
    const mul = PRICE_MUL_BY_STANDING[f.standing(this.faction)];
    const out: VendorOffer[] = [];
    for (const o of this.offers) {
      o.sold = vendorSold(run, o.id);
      if (o.sold || o.depth > tier) continue;
      o.price = Math.max(1, Math.round(basePrice(o) * mul));
      out.push(o);
    }
    return out;
  }

  status(run: RunState): VendorStatus {
    const f = runFactions(run);
    const shelf = this.available(run);
    const open = f.shopOpen(this.faction);
    const offer = this.inRange && shelf.length > 0 ? shelf[this.cursor % shelf.length] : null;
    const locked = this.offers.filter(
      (o) => !vendorSold(run, o.id) && o.depth > f.offerTier(this.faction),
    ).length;

    return {
      faction: this.faction,
      name: getFaction(this.faction).name,
      standing: f.standing(this.faction),
      reputation: f.reputation(this.faction),
      shopOpen: open,
      hostile: f.hostile(this.faction),
      inRange: this.inRange,
      offer,
      timeToRotate: Math.max(0, this.timer),
      stock: shelf.length,
      locked,
      scrip: run.inventory.scrip,
      affordable: offer !== null && run.inventory.scrip >= offer.price,
      knownFor: this.knownFor,
      greeting: this.greeting(run),
    };
  }

  /**
   * Take what is on the counter.
   *
   * The refusals are all *before* the debit and each says why, because
   * `spendScrip` deliberately moves nothing when it cannot cover the cost — a
   * partial debit is worse than a refused one — and a silent refusal reads to
   * the player as a broken button.
   */
  buy(run: RunState): VendorPurchase {
    const f = runFactions(run);
    if (!this.inRange) {
      return { ok: false, message: 'too far from the counter', outcome: null, paid: 0 };
    }
    if (f.hostile(this.faction)) {
      return {
        ok: false,
        message: `${getFaction(this.faction).name} do not deal with you — they hunt you`,
        outcome: null,
        paid: 0,
      };
    }
    if (!f.shopOpen(this.faction)) {
      return {
        ok: false,
        message: `${getFaction(this.faction).name} will not trade with you`,
        outcome: null,
        paid: 0,
      };
    }
    const shelf = this.available(run);
    if (shelf.length === 0) {
      return { ok: false, message: 'the counter is bare', outcome: null, paid: 0 };
    }
    const offer = shelf[this.cursor % shelf.length];
    if (!spendScrip(run, offer.price)) {
      return {
        ok: false,
        message: `${offer.name} costs ${offer.price} scrip — you have ${run.inventory.scrip}`,
        outcome: null,
        paid: 0,
      };
    }
    // Ledger *after* the debit, and only if the debit succeeded: the two halves
    // of a transaction must not be able to disagree about whether it happened.
    markVendorPurchase(run, offer.id);
    // And the run's own record of what it acquired (#107). Inside the purchase
    // rather than beside it at the integrator, for the reason `recordKill` pays
    // its own Ash: `addModule` sat unwired for the whole project precisely
    // because recording an acquisition was somebody else's line to remember.
    // `outcome` below is the *instruction* to combat/, which is a different
    // question — that says "socket this", this says "the run found one".
    addModule(run, offer.moduleId);
    offer.sold = true;
    this.timer = VENDOR_ROTATE_SECONDS;
    return {
      ok: true,
      message: `bought ${offer.name} for ${offer.price} scrip`,
      outcome: { kind: 'module', moduleId: offer.moduleId },
      paid: offer.price,
    };
  }

  /**
   * The consequence of hostility, beyond a closed door.
   *
   * The issue asks for this specifically: "a faction that hates you having
   * presence in the world is more interesting than a closed door." So the stall
   * is still there, and so are they. Returned as a request rather than spawned,
   * because world/ owns no entity registry — and returned exactly once, because
   * an ambush that re-arms every frame is a wall of enemies rather than an
   * event.
   */
  takeAmbush(run: RunState): AmbushRequest | null {
    if (this.ambushed || !this.inRange) return null;
    const f = runFactions(run);
    if (!f.hostile(this.faction)) return null;
    this.ambushed = true;
    // Scaled by how far past hostile they are, so a nemesis is worse than a
    // faction that has only just turned.
    const span = AMBUSH_MAX - AMBUSH_MIN;
    const count = AMBUSH_MIN + Math.round(f.hostileEncounterRate(this.faction) * span);
    return { faction: this.faction, count, x: this.x, y: this.y };
  }

  private greeting(run: RunState): string {
    const f = runFactions(run);
    const name = getFaction(this.faction).name;
    if (f.hostile(this.faction)) return `${name} — they were waiting for you`;
    if (!f.shopOpen(this.faction)) return `${name} — the shutters are down`;
    const tier = f.offerTier(this.faction);
    if (tier >= 3) return `${name} — everything they have is yours`;
    if (tier === 2) return `${name} — they bring out the good stock`;
    return `${name} — they will trade`;
  }
}

// ------------------------------------------------------------------ helpers

function basePrice(o: VendorOffer): number {
  // Depth is a standing gate, not a quality gate, so it does not price the
  // item — rarity does. A relic you unlocked by devotion still costs relic
  // money; what devotion buys is the discount, and that is the standing
  // multiplier's job.
  return PRICE_BY_RARITY[o.rarity] ?? PRICE_BY_RARITY.common;
}

/**
 * The vendor's whole shelf, deterministic in the placement.
 *
 * Built at full depth once and gated by standing on read, rather than
 * regenerated when reputation moves: a shop whose contents change identity as
 * you gain favour is a shop where the thing you were saving for disappears.
 * Standing widens the shelf; it never redraws it.
 */
function buildStock(
  placement: VendorPlacement,
  families: readonly ModuleFamily[],
  modules: ReadonlyMap<string, ModuleDef>,
): VendorOffer[] {
  const byFamily = new Map<ModuleFamily, ModuleDef[]>();
  for (const m of modules.values()) {
    const list = byFamily.get(m.family);
    if (list) list.push(m);
    else byFamily.set(m.family, [m]);
  }

  const out: VendorOffer[] = [];
  for (let fi = 0; fi < families.length; fi++) {
    const pool = byFamily.get(families[fi]);
    if (!pool || pool.length === 0) continue;
    // Sorted so the shelf does not depend on the registry's insertion order.
    const sorted = [...pool].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
    const h = hash2(placement.band * 31 + fi, placement.index, 0x0ffe45);
    const start = h % sorted.length;
    const take = Math.min(MAX_PER_FAMILY, sorted.length);
    for (let k = 0; k < take; k++) {
      const m = sorted[(start + k) % sorted.length];
      if (out.some((o) => o.moduleId === m.id)) continue;
      out.push({
        id: `${placement.id}:${m.id}`,
        moduleId: m.id,
        name: m.name,
        family: m.family,
        rarity: m.rarity,
        desc: m.desc,
        price: PRICE_BY_RARITY[m.rarity] ?? PRICE_BY_RARITY.common,
        depth: (k + 1) as 1 | 2 | 3,
        sold: false,
      });
    }
  }
  return out;
}
